import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, ArrowUp, ArrowDown, RefreshCw, Shapes } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { CategoryDto, CategoryRequestDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  RichTextInput,
  ColorInput,
  IconPicker,
  getCategoryIcon,
  Switch,
  Modal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.75rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const NameText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

/** Rounded chip that previews the category icon tinted with its own color. */
const IconChip = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};
  color: ${(p) => p.$color || p.theme.primary};
`;

/** Status toggle styled as a bordered pill, matching the Usuários screen. */
const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

/** Friendly Portuguese copy for the business errors raised by the core. */
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  CATEGORY_SLUG_EXISTS: 'Já existe uma categoria com esse slug. Escolha outro identificador.',
};

const DELETE_ERROR_MESSAGES: Record<string, string> = {
  CATEGORY_IN_USE:
    'Esta categoria não pode ser excluída porque há empresas vinculadas a ela. Mova essas empresas para outra categoria antes de excluir.',
};

/** Blank category form used when creating a new record. */
const EMPTY_FORM: CategoryRequestDto = {
  name: '',
  slug: '',
  description: '',
  icon: '',
  color: '',
  active: true,
  displayOrder: 0,
};

/** Build a request payload from an existing category (used when reordering). */
const toRequest = (c: CategoryDto): CategoryRequestDto => ({
  name: c.name,
  slug: c.slug,
  description: c.description ?? '',
  icon: c.icon ?? '',
  color: c.color ?? '',
  active: c.active,
  displayOrder: c.displayOrder,
});

/** Category icon preview shown in the list, falling back to a generic shape. */
const CategoryIconChip: React.FC<{ icon?: string; color?: string }> = ({ icon, color }) => {
  const Icon = getCategoryIcon(icon);
  return (
    <IconChip $color={color || undefined} aria-hidden>
      {Icon ? <Icon size={16} /> : <Shapes size={16} />}
    </IconChip>
  );
};

const CategoriesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryRequestDto>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<CategoryDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);
  const [reordering, setReordering] = useState<boolean>(false);

  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.listCategories({ size: 100 }),
    []
  );

  const all = data?.content ?? [];
  const term = search.trim().toLowerCase();
  const isFiltered = term !== '' || statusFilter !== '';
  const filtered = all.filter((c) => {
    const matchesText =
      !term || c.name.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term);
    const matchesStatus =
      statusFilter === '' || (statusFilter === 'active' ? c.active : !c.active);
    return matchesText && matchesStatus;
  });
  const stats = {
    total: all.length,
    active: all.filter((c) => c.active).length,
    inactive: all.filter((c) => !c.active).length,
    merchants: all.reduce((sum, c) => sum + (c.merchantsCount ?? 0), 0),
  };

  const openCreate = (): void => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, displayOrder: all.length });
    setIsOpen(true);
  };

  const openEdit = (category: CategoryDto): void => {
    setEditingId(category.id);
    setForm(toRequest(category));
    setIsOpen(true);
  };

  const update = <K extends keyof CategoryRequestDto>(key: K, value: CategoryRequestDto[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      if (editingId) {
        await backofficeApi.updateCategory(editingId, form);
        showNotification('Categoria atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createCategory(form);
        showNotification('Categoria criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar a categoria.', SAVE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteCategory(deleting.id);
      showNotification('Categoria excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível excluir a categoria.', DELETE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setRemoving(false);
    }
  };

  /** Move a category one position up (-1) or down (+1) and persist the new order. */
  const move = async (index: number, direction: -1 | 1): Promise<void> => {
    const items = [...all];
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    setReordering(true);
    try {
      await backofficeApi.reorderCategories(items.map((c) => c.id));
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível reordenar as categorias.'), 'error');
    } finally {
      setReordering(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Categorias"
        subtitle="Gerencie as categorias de empresas disponíveis na plataforma."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova categoria
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total de categorias" value={formatNumber(stats.total)} />
        <StatCard label="Ativas" value={formatNumber(stats.active)} />
        <StatCard label="Inativas" value={formatNumber(stats.inactive)} />
        <StatCard label="Empresas vinculadas" value={formatNumber(stats.merchants)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome ou slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhuma categoria encontrada"
        emptyDescription="Crie a primeira categoria para organizar as empresas."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Ordem</th>
                <th>Nome</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Empresas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((category, index) => (
                <tr key={category.id}>
                  <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <RowActions style={{ justifyContent: 'flex-start', gap: '0.25rem' }}>
                      <ActionButton
                        aria-label="Mover para cima"
                        title="Mover para cima"
                        disabled={index === 0 || reordering || isFiltered}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUp size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Mover para baixo"
                        title="Mover para baixo"
                        disabled={index === filtered.length - 1 || reordering || isFiltered}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDown size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                  <td>
                    <NameCell>
                      <CategoryIconChip icon={category.icon} color={category.color} />
                      <NameText>
                        <strong>{category.name}</strong>
                        <span>{category.slug}</span>
                      </NameText>
                    </NameCell>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={category.active ? 'success' : 'secondary'}>
                      {category.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(category.merchantsCount ?? 0)}</td>
                  <td>
                    <RowActions>
                      <ActionButton aria-label="Editar categoria" title="Editar" onClick={() => openEdit(category)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Excluir categoria"
                        title="Excluir"
                        color="#DC2626"
                        onClick={() => setDeleting(category)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar categoria' : 'Nova categoria'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex.: Restaurantes"
        />
        <TextInput
          label="Slug"
          value={form.slug}
          onChange={(e) => update('slug', e.target.value)}
          placeholder="Ex.: restaurantes"
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <RichTextInput
          label="Descrição"
          value={form.description ?? ''}
          onChange={(html) => update('description', html)}
          placeholder="Descreva a categoria (ex.: o que agrupa, exemplos de empresas)…"
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <IconPicker
          label="Ícone"
          value={form.icon ?? ''}
          onChange={(iconName) => update('icon', iconName)}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <ColorInput
          label="Cor"
          value={form.color ?? ''}
          onChange={(hex) => update('color', hex)}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <ToggleField>
          <Switch checked={form.active} onChange={(v) => update('active', v)} />
          Status ativo
        </ToggleField>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir categoria"
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Tem certeza que deseja excluir a categoria <strong>{deleting?.name}</strong>? Essa ação não
          poderá ser desfeita.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default CategoriesPage;
